import { createTheme, colors, Theme } from '@mui/material'

// Function to create a palette from any client theme
const createPaletteFromClientTheme = (clientTheme: any) => {
    const white = '#FFFFFF'
    const black = '#000000'

    return {
        white,
        black,
        primary: {
            contrastText: clientTheme.primaryText ?? white,
            dark: clientTheme.primaryDark,
            main: clientTheme.primary,
            light: clientTheme.primaryLight,
        },
        secondary: {
            contrastText: clientTheme.secondaryText ?? white,
            dark: clientTheme.secondaryDark,
            main: clientTheme.secondary,
            light: clientTheme.secondaryLight,
        },
        success: {
            contrastText: white,
            dark: colors.green[900],
            main: colors.green[600],
            light: colors.green[400],
        },
        info: {
            contrastText: white,
            dark: colors.blue[900],
            main: colors.blue[600],
            light: colors.blue[400],
        },
        warning: {
            contrastText: white,
            dark: colors.orange[900],
            main: colors.orange[600],
            light: colors.orange[400],
        },
        error: {
            contrastText: white,
            dark: colors.red[900],
            main: colors.red[600],
            light: colors.red[400],
        },
        text: {
            primary: clientTheme.typography?.fontColor?.primaryText
                ? clientTheme.typography.fontColor.primaryText
                : colors.blueGrey[900],
            secondary: clientTheme.typography?.fontColor?.secondaryText
                ? clientTheme.typography.fontColor.secondaryText
                : colors.blueGrey[600],
            link: colors.blue[600],
        },
        background: {
            default: clientTheme.mainLayout.backgroundColor,
            paper: white,
            bg: '#fff',
        },
        icon: colors.blueGrey[600],
        divider: colors.grey[200],
        textAltGray: colors.grey[500],
        topBar: colors.grey[500],
        sidebar: {
            primary: clientTheme.sidebar.backgroundColor,
            secondary: '#1f2024',
            active: 'red',
            hover: {
                primary: '#3f4143',
                secondary: '#D3ECF9',
            },
        },
    }
}

// Function to create typography from any client theme
const createTypographyFromClientTheme = (clientTheme: any, palette: any) => {
    // Determine font family from fontFamilyConfig if available, otherwise use the array
    const fontFamily = clientTheme.typography.fontFamilyConfig
        ? `${clientTheme.typography.fontFamilyConfig.primary}, ${clientTheme.typography.fontFamilyConfig.fallback}`
        : clientTheme.typography.fontFamily.join(',')

    return {
        fontFamily,
        h1: {
            color: palette.text.primary,
            fontWeight: 500,
            fontSize: clientTheme.typography.h1.fontSize,
            letterSpacing: '-0.24px',
            lineHeight: '40px',
        },
        h2: {
            color: palette.text.primary,
            fontWeight: 500,
            fontSize: clientTheme.typography.h2.fontSize,
            letterSpacing: '-0.24px',
            lineHeight: '32px',
        },
        h3: {
            color: palette.text.primary,
            fontWeight: 500,
            fontSize: clientTheme.typography.h3.fontSize,
            letterSpacing: '-0.06px',
            lineHeight: '28px',
        },
        h4: {
            color: palette.text.primary,
            fontWeight: 500,
            fontSize: clientTheme.typography.h4.fontSize,
            letterSpacing: '-0.06px',
            lineHeight: '24px',
        },
        h5: {
            color: palette.text.primary,
            fontWeight: 500,
            fontSize: clientTheme.typography.h5.fontSize,
            letterSpacing: '-0.05px',
            lineHeight: '20px',
        },
        h6: {
            color: palette.text.primary,
            fontWeight: 500,
            fontSize: clientTheme.typography.h6.fontSize,
            letterSpacing: '-0.05px',
            lineHeight: '20px',
        },
        subtitle1: {
            color: palette.text.primary,
            fontSize: clientTheme.typography.subtitle1.fontSize,
            letterSpacing: '-0.05px',
            lineHeight: '25px',
        },
        subtitle2: {
            color: palette.text.secondary,
            fontWeight: 400,
            fontSize: clientTheme.typography.subtitle2.fontSize,
            letterSpacing: '-0.05px',
            lineHeight: '21px',
        },
        body1: {
            color: palette.text.primary,
            fontSize: clientTheme.typography.body1.fontSize,
            letterSpacing: '-0.05px',
            lineHeight: '21px',
        },
        body2: {
            color: palette.text.secondary,
            fontSize: clientTheme.typography.body2.fontSize,
            letterSpacing: '-0.04px',
            lineHeight: '18px',
        },
        button: {
            color: palette.text.primary,
            fontSize: clientTheme.typography.button1.fontSize,
        },
        caption: {
            color: palette.text.secondary,
            fontSize: clientTheme.typography.caption.fontSize,
            letterSpacing: '0.33px',
            lineHeight: '13px',
        },
        overline: {
            color: palette.text.secondary,
            fontSize: clientTheme.typography.overline.fontSize,
            fontWeight: 500,
            letterSpacing: '0.33px',
            lineHeight: '13px',
        },
    }
}

// Main function to create a MUI theme from any client theme
export const createMuiThemeFromClientTheme = (clientTheme: any): Theme => {
    const palette = createPaletteFromClientTheme(clientTheme)
    const typography = createTypographyFromClientTheme(clientTheme, palette)

    // Create the final theme with components overrides
    return createTheme({
        palette,
        typography,
        components: {
            // Button overrides
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontFamily: clientTheme.typography.fontFamily.join(','),
                        fontWeight: 600
                    },
                    textSecondary: {
                        color: clientTheme.secondaryText,
                    },
                },
            },
            // Paper overrides
            MuiPaper: {
                styleOverrides: {
                    elevation1: {
                        boxShadow:
                            '0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15)',
                    },
                },
            },
            // Table cell overrides
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        ...typography.body1,
                        borderBottom: `1px solid ${palette.divider}`,
                    },
                },
            },
            // Table head overrides
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.grey[50],
                    },
                },
            },
            // Table row overrides
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&.Mui-selected': {
                            backgroundColor: palette.background.default,
                        },
                        '&:hover': {
                            backgroundColor: palette.background.default,
                        },
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    color: 'secondary',
                },
            },
        },
        zIndex: {
            appBar: 1200,
            drawer: 1100,
        },
    })
}

// For backward compatibility
export const theme1 = createMuiThemeFromClientTheme(
    require('theme-exports').clientTheme1
)
export const theme2 = createMuiThemeFromClientTheme(
    require('theme-exports').clientTheme2
)
